import { writeFamily } from '../config';
/**
 * Resolves a surface's DM-scope per-agent conversation id, from the endpoint
 * that serves that surface's write family (ADR-090 D4a + N1).
 *
 * ⚠️ **This class used to say "the mini-app's", and that was the whole bug.** It
 * was written mini-app-first and never re-scoped when web-ui and desktop were
 * wired to it — so those surfaces called `ResolveConversation`, which is
 * explicitly a mini-app-only route (gateway `clientconversation.go`: *"web/desktop
 * tokens (and any pre-tg_uid session) carry no tg_uid, so there is no DM scope to
 * resolve"*). It refuses them with `MINIAPP_TGUID_MISSING`, which no OIDC shell
 * routes anywhere. The file stated its own limitation at the top and the
 * violating callers were added anyway.
 *
 * The two families now take the endpoint that serves them:
 *
 * | write family | surfaces | endpoint | id derivation |
 * |---|---|---|---|
 * | `APP` | web, desktop (and mobile, which is `'web'`) | `POST /v1/conversations:app-dm` | `AppDMID(claims.UserID, agent_id)` |
 * | `TG`  | telegram (mini-app) | `POST /v1/conversations:resolve` | `dmConversationID(tg_uid, agent_id)` |
 *
 * Both are **server-derived from a single funnel** — the client never replicates
 * the uuidv5 in either family, so read == write by construction with no drift.
 *
 * Under per-agent conversation identity, agent-selection IS conversation-
 * selection: each agent tab has its OWN conversation + history.
 *
 * This replaces the pre-ADR-090 model — ONE shared (chat,user,thread)
 * conversation + a mutable `focused_agent_id` pointer, discovered by listing the
 * user's conversations and taking the most-recently-active. Two Dmitry live-test
 * bugs came from that heuristic and BOTH close here:
 *   - #2b group-topic bleed: most-recent-active surfaced his Telegram GROUP topic
 *     ("KB Inbox") on the personal surface. resolve(agent) now returns that
 *     agent's DM conversation; group-topic threads are unreachable from here.
 *   - per-agent gap ("doesn't switch on agent"): one shared thread never changed
 *     on agent switch. resolve(agentA) and resolve(agentB) now return DIFFERENT
 *     ids → the tab reloads that agent's own history.
 *
 * Read-side only (ADR-090 D4a): the write-side per-agent mint lives on the O1'
 * gateway path; this does not touch it. The deprecated client SetFocusedAgent is
 * NO LONGER called — agent-switch is a resolve, not a focus mutation.
 */
export class ConversationDirectory {
    client;
    /** One resolve per agent, memoized — the id is stable for a given agent. */
    byAgent = new Map();
    /**
     * The write family this directory addresses, fixed at construction from the
     * surface.
     *
     * Derived via `writeFamily` rather than taken as a family directly, and that is
     * deliberate: `writeFamily` is an exhaustive switch with a `never` guard
     * (`config.ts`, added by web!330) so a NEW surface is a compile error until
     * someone classifies it. Accepting a family here instead would let a caller
     * pass one, and the asymmetry that guard exists for is that a new TG-family
     * shell defaulting to `APP` is the dangerous direction. One classifier, one
     * place, one guard.
     */
    family;
    /**
     * `surface` is REQUIRED — no default. A default would silently classify the
     * next surface wired to this class, which is exactly how web-ui and desktop
     * ended up on the mini-app's endpoint.
     */
    constructor(client, surface) {
        this.client = client;
        this.family = writeFamily(surface);
    }
    /**
     * The selected agent's DM-scope conversation id. Concurrent calls for the same
     * agent share one in-flight request; a failed resolve is NOT cached, so a
     * transient error stays retryable on the next selection.
     *
     * Memoizing the APP arm is as correct as memoizing the TG one even though it
     * calls a WRITE: the app-DM mint is idempotent by construction (the id is
     * derived server-side from `(claims.UserID, agent_id)`, and the row +
     * participant are created-or-returned in one transaction), so a second call
     * cannot produce a second conversation. `forget()` is safe for the same reason.
     */
    resolve(agentId) {
        const cached = this.byAgent.get(agentId);
        if (cached)
            return cached;
        const pending = (this.family === 'APP'
            ? this.client.conversations
                .mintAppDm(agentId)
                // MintAppDMResponse carries the whole Conversation, not a bare id.
                .then(({ conversation }) => conversation?.id)
            : this.client.conversations
                .resolveConversation(agentId)
                .then(({ conversationId }) => conversationId))
            .then((id) => {
            if (!id) {
                // Named per arm rather than shared: the two responses fail in different
                // FIELDS, and a message pointing at the wrong one sends the next reader
                // to the wrong handler.
                throw new Error(this.family === 'APP'
                    ? 'MintAppDM returned no conversation'
                    : 'ResolveConversation returned no conversation_id');
            }
            return id;
        })
            .catch((err) => {
            this.byAgent.delete(agentId);
            throw err;
        });
        this.byAgent.set(agentId, pending);
        return pending;
    }
    /**
     * Drop the memoized id for one agent, so the next `resolve` is a real
     * round-trip.
     *
     * Exists for exactly one caller: the ADR-092 CONVERSATION_AGENT_MISMATCH
     * recovery. The gateway derives the DM conversation as a pure function of the
     * caller's identity and the agent — `dmConversationID(tg_uid, agent_id)` for
     * the TG family, `AppDMID(claims.UserID, agent_id)` for the APP family, both in
     * gateway-service `internal/adapter/grpc/addressing.go` — and refuses a send
     * whose conversation_id is not that value. Since this class is the only source
     * of those ids, a mismatch means the MEMO is stale relative to the token, which
     * a re-resolve fixes and a plain retry cannot: `resolve` would hand back the
     * same refused id forever.
     *
     * Successful resolves are memoized for the session with no TTL (the id is
     * stable for a given agent AND a given caller identity), so the staleness this
     * clears comes from that identity changing under a warm client — the `tg_uid`
     * in the TG family, `claims.UserID` in the APP family. A session-identity
     * switch, not the passage of time. That is why this is an explicit,
     * refusal-driven purge and not an expiry: nothing client-side can observe
     * the change that invalidates the entry, so only the server's refusal can
     * tell us the memo is wrong.
     *
     * Not exposed as "clear everything" on purpose — a mismatch is evidence
     * about ONE (agent, token) pair. Purging the whole map would also discard
     * entries that are still correct and turn a targeted recovery into a
     * session-wide round-trip storm on the next agent switch.
     */
    forget(agentId) {
        this.byAgent.delete(agentId);
    }
}
//# sourceMappingURL=conversations.js.map